import React, { useState, useEffect } from 'react';
import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Globe from '../components/Globe';
import YapperModal from '../components/YapperModal';
import { Button } from '../components/ds/core/Button';
import { Card } from '../components/ds/core/Card';
import './GlobeView.css';

const SKIN_GLOBE = {
  wired: { earthColor: '#1a5e3c', oceanColor: '#0c2018', bgColor: '#0b1410', markerColor: 0xffb000, graticuleColor: 'rgba(120,255,190,0.22)', glowColor: 0x1affc6, selectedColor: '#ff2b4e', flat: true },
  moe: { earthColor: '#b6f2a8', oceanColor: '#c2ecff', bgColor: null, markerColor: 0xff4b9b, graticuleColor: 'rgba(255,255,255,0.4)', glowColor: 0xffb3e6, selectedColor: '#ffe27a', coastColor: 'rgba(255,255,255,0.85)', coastWidth: 1.4, flat: true },
  daylight: { earthColor: '#2faf68', oceanColor: '#145fb0', bgColor: null, glowColor: 0xffffff, glowOpacity: 0.5, flat: false },
  penpal: { earthColor: '#cdbe96', oceanColor: '#f1ead8', bgColor: null, markerColor: 0xb4452b, graticuleColor: 'rgba(120,100,60,0.28)', glowColor: 0xd9cdb0, glowOpacity: 0.4, selectedColor: '#ffd54a', coastColor: 'rgba(60,70,90,0.85)', coastWidth: 1.6, ambient: 1.35, sunIntensity: 0.4, flat: false },
};

function GlobeView({ user, bgColor, skin, setUser, setBgColor, setNavbarColor, setBgImage, setNavbarImage }) {
  const sg = SKIN_GLOBE[skin];
  const [showWelcome, setShowWelcome] = useState(localStorage.getItem('hideWelcomeInfo') !== 'true');
  const [isAdmin, setIsAdmin] = useState(false);
  const [userViewMode, setUserViewMode] = useState(localStorage.getItem('staffUserViewMode') === 'true');
  const [showMassMessageModal, setShowMassMessageModal] = useState(false);
  const [massMessage, setMassMessage] = useState('');
  const [sendingMassMessage, setSendingMassMessage] = useState(false);
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info', onConfirm: null, showCancel: false });
  const [wiredClock, setWiredClock] = useState('');
  const [wiredLog, setWiredLog] = useState([]);

  useEffect(() => {
    if (skin !== 'wired') return;
    const tick = () => {
      const d = new Date();
      const p = (n) => String(n).padStart(2, '0');
      setWiredClock(`${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`);
    };
    tick();
    const clockId = setInterval(tick, 1000);
    const lines = [
      '> handshake.................ok',
      '> protocol7.layer..........bound',
      '> entity.trace.............active',
      '> anonymity................[null]',
      '> presence.................you',
    ];
    setWiredLog([]);
    let i = 0;
    const logId = setInterval(() => {
      i++;
      setWiredLog(lines.slice(0, i));
      if (i >= lines.length) clearInterval(logId);
    }, 420);
    return () => { clearInterval(clockId); clearInterval(logId); };
  }, [skin]);

  const showAlert = (title, message, type = 'info') => {
    setModal({ isOpen: true, title, message, type, onConfirm: () => setModal(m => ({ ...m, isOpen: false })), showCancel: false });
  };

  const showConfirm = (title, message, onConfirmCallback, type = 'warning') => {
    setModal({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: () => {
        setModal(m => ({ ...m, isOpen: false }));
        if (onConfirmCallback) onConfirmCallback();
      },
      showCancel: true
    });
  };

  useEffect(() => {
    checkAdminStatus();
  }, [user]);

  // Listen for staff view mode changes
  useEffect(() => {
    const handleStaffViewModeChange = () => {
      setUserViewMode(localStorage.getItem('staffUserViewMode') === 'true');
    };

    window.addEventListener('staffViewModeChanged', handleStaffViewModeChange);
    return () => window.removeEventListener('staffViewModeChanged', handleStaffViewModeChange);
  }, []);

  const checkAdminStatus = async () => {
    try {
      const userId = localStorage.getItem('userId');
      if (userId) {
        const adminDoc = await getDoc(doc(db, 'admins', userId));
        setIsAdmin(adminDoc.exists());
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const handleCloseWelcome = () => {
    localStorage.setItem('hideWelcomeInfo', 'true');
    setShowWelcome(false);
  };

  const handleSendMassMessage = async (e) => {
    e.preventDefault();
    if (!massMessage.trim()) return;

    showConfirm('Post Announcement', `Are you sure you want to post this announcement?\n\nMessage: "${massMessage.trim()}"`, async () => {
      setSendingMassMessage(true);
      try {
        const adminId = localStorage.getItem('userId');
        const adminDoc = await getDoc(doc(db, 'users', adminId));
        const adminUsername = adminDoc.data()?.username || adminDoc.data()?.nickname || '';

        // Post announcement to public announcements collection
        await addDoc(collection(db, 'announcements'), {
          text: massMessage.trim(),
          adminId: adminId,
          adminUsername: adminUsername,
          timestamp: serverTimestamp()
        });

        setMassMessage('');
        setShowMassMessageModal(false);
        showAlert('Success', 'Announcement posted successfully! Everyone can see it in the Announcements tab.', 'success');
      } catch (error) {
        console.error('Error posting announcement:', error);
        showAlert('Error', 'Error posting announcement: ' + error.message, 'error');
      } finally {
        setSendingMassMessage(false);
      }
    });
  };

  return (
    <div className="globe-view">
      {skin === 'wired' && (
        <div className="wired-hud" aria-hidden="true">
          <div className="wired-title">yapp<span className="wired-jp"> つながり</span></div>
          <div className="wired-sub wired-glitch" data-t="// WIRED">// WIRED</div>
          <div className="wired-rule" />
          <div className="wired-blink">PRESENT DAY&nbsp;&nbsp;PRESENT TIME</div>
        </div>
      )}
      {skin === 'wired' && (
        <div className="wired-hud wired-hud-right" aria-hidden="true">
          <div className="wired-label">NODE&nbsp;//&nbsp;NAVI-07</div>
          <div className="wired-clock">{wiredClock}</div>
          <div className="wired-rule" />
          <pre className="wired-term">{wiredLog.join('\n')}</pre>
        </div>
      )}
      {skin === 'wired' && (
        <div className="wired-hud wired-hud-legend" aria-hidden="true">
          <div><span className="wired-beacon">◉</span> ORIGIN BEACON — incoming transmission</div>
          <div><span className="wired-traced">▣</span> TRACED — locked, click to decode</div>
          <div className="wired-legend-hint">drag to rotate the sphere · scroll to dive</div>
        </div>
      )}
      <Globe
        earthColor={sg ? sg.earthColor : (user?.themeEarthColor || '#2faf68')}
        oceanColor={sg ? sg.oceanColor : (user?.themeOceanColor || '#145fb0')}
        bgColor={sg && sg.bgColor ? sg.bgColor : bgColor}
        markerColor={sg ? sg.markerColor : 0x667eea}
        graticuleColor={sg ? sg.graticuleColor : undefined}
        glowColor={sg ? sg.glowColor : undefined}
        glowOpacity={sg ? sg.glowOpacity : undefined}
        selectedColor={sg ? sg.selectedColor : undefined}
        coastColor={sg ? sg.coastColor : undefined}
        coastWidth={sg ? sg.coastWidth : undefined}
        ambient={sg ? sg.ambient : undefined}
        sunIntensity={sg ? sg.sunIntensity : undefined}
        flat={sg ? sg.flat : false}
        skin={skin}
        setBgColor={setBgColor}
        setNavbarColor={setNavbarColor}
        setBgImage={setBgImage}
        setNavbarImage={setNavbarImage}
        setUser={setUser}
      />
      {showWelcome && (
        <Card
          variant="glass"
          padding="none"
          className="globe-info"
          style={{ transform: 'translate(-50%, -50%)' }}
        >
          <button className="close-btn info-close" onClick={handleCloseWelcome}>×</button>
          <h2>Welcome, {user?.username || localStorage.getItem('userNickname') || 'Explorer'}!</h2>
          <p>Connect with people around the world. Send anonymous messages and make new friends!</p>
        </Card>
      )}
      {isAdmin && !userViewMode && (
        <button
          className="mass-message-toggle-btn"
          onClick={() => setShowMassMessageModal(true)}
          title="Send Mass Message (Admin Only)"
        >
          💬
        </button>
      )}

      {/* Mass Message Modal */}
      {showMassMessageModal && (
        <div className="mass-message-modal-overlay" onClick={() => setShowMassMessageModal(false)}>
          <div className="mass-message-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal-btn" onClick={() => setShowMassMessageModal(false)}>×</button>
            <h2>📢 Post Announcement</h2>
            <p className="modal-subtitle">This announcement will be visible to everyone</p>
            <form onSubmit={handleSendMassMessage}>
              <textarea
                placeholder="Type your announcement here..."
                value={massMessage}
                onChange={(e) => setMassMessage(e.target.value)}
                disabled={sendingMassMessage}
                maxLength={1000}
                rows={5}
              />
              <div className="modal-actions">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowMassMessageModal(false)}
                  disabled={sendingMassMessage}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={sendingMassMessage || !massMessage.trim()}
                >
                  {sendingMassMessage ? '⏳ Posting...' : '📤 Post Announcement'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <YapperModal
        isOpen={modal.isOpen}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onConfirm={modal.onConfirm}
        onCancel={modal.showCancel ? () => setModal(m => ({ ...m, isOpen: false })) : null}
      />
    </div>
  );
}

export default GlobeView;
